import {
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  Textarea,
  Checkbox,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@design-system';
import { QuestionCombobox } from '@/features/quote-and-apply-builder';
import { Plus, X } from 'lucide-react';
import { useGroupSettings } from '@/features/quote-and-apply-builder';

interface QuestionGroupSettingsDialogProps {
  open: boolean;
  group: PolicyFormQuestionGroup;
  questions: Record<string, PolicyFormQuestion>;
  onSave: (updates: Partial<PolicyFormQuestionGroup>) => void;
  onClose: () => void;
}

export function QuestionGroupSettingsDialog({
  open,
  group,
  questions,
  onSave,
  onClose,
}: QuestionGroupSettingsDialogProps) {
  const {
    title,
    setTitle,
    description,
    setDescription,
    format,
    setFormat,
    align,
    setAlign,
    className,
    setClassName,
    hidden,
    setHidden,
    hasCondition,
    setHasCondition,
    conditionType,
    setConditionType,
    simpleCondition,
    setSimpleCondition,
    logicalCondition,
    setLogicalCondition,
    questionOptions,
    selectedQuestion,
    showValueInput,
    comparisonOperators,
    groupFormats,
    groupAlignments,
    handleSave,
    handleAddLogicalCondition,
    handleUpdateLogicalCondition,
    handleRemoveLogicalCondition,
  } = useGroupSettings({ group, questions, onSave, onClose });

  return (
    <Dialog
      open={open}
      onOpenChange={onClose}
    >
      <DialogContent className="max-h-[95vh] gap-6 overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Question Group Settings</DialogTitle>
        </DialogHeader>

        <div className="space-y-6">
          <div className="space-y-2">
            <Label htmlFor="group-title">Group Title</Label>
            <Input
              value={title}
              onChange={event => setTitle(event.target.value)}
              placeholder="Enter group title (optional)"
              id="group-title"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="group-description">Description</Label>
            <Textarea
              value={description}
              onChange={event => setDescription(event.target.value)}
              placeholder="Enter group description (optional)"
              id="group-description"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="group-layout">Layout</Label>
              <Select
                value={format}
                onValueChange={value => setFormat(value as typeof format)}
              >
                <SelectTrigger
                  id="group-layout"
                  className="w-full"
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {groupFormats.map(formatOption => (
                    <SelectItem
                      key={formatOption.value}
                      value={formatOption.value}
                    >
                      {formatOption.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="group-alignment">Alignment</Label>
              <Select
                value={align || 'none'}
                onValueChange={value =>
                  setAlign(value === 'none' ? undefined : (value as typeof align))
                }
              >
                <SelectTrigger
                  id="group-alignment"
                  className="w-full"
                >
                  <SelectValue placeholder="Default" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Default</SelectItem>
                  {groupAlignments.map(alignOption => (
                    <SelectItem
                      key={alignOption.value}
                      value={alignOption.value}
                    >
                      {alignOption.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="group-classes">Custom Style Classes</Label>
            <Input
              value={className}
              onChange={event => setClassName(event.target.value)}
              placeholder="e.g., grid-cols-3, gap-8"
              id="group-classes"
            />
            <p className="text-xs text-gray-500">
              Add style classes to customize this group's appearance (advanced)
            </p>
          </div>

          <div className="flex items-start gap-3">
            <Checkbox
              id="hidden"
              checked={hidden}
              onCheckedChange={checked => setHidden(!!checked)}
              className="mt-0.5"
            />
            <div className="flex flex-col gap-1">
              <Label htmlFor="hidden">Hidden Group</Label>
              <p className="text-xs text-gray-500">
                Group will be hidden but its data will still be processed
              </p>
            </div>
          </div>

          <div className="space-y-6 border-t pt-4">
            <div className="space-y-2">
              <Label htmlFor="show-hide-rule">Show/Hide Rule</Label>
              <Select
                value={hasCondition ? 'yes' : 'no'}
                onValueChange={value => setHasCondition(value === 'yes')}
              >
                <SelectTrigger
                  id="show-hide-rule"
                  className="w-full"
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="no">Always Show</SelectItem>
                  <SelectItem value="yes">Conditional</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {hasCondition && (
              <div className="space-y-6 rounded-lg border p-4">
                <div className="space-y-2">
                  <Label htmlFor="rule-type">Rule Type</Label>
                  <Select
                    value={conditionType}
                    onValueChange={value => setConditionType(value as 'simple' | 'logical')}
                  >
                    <SelectTrigger
                      id="rule-type"
                      className="w-full"
                    >
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="simple">Single Rule</SelectItem>
                      <SelectItem value="logical">Multiple Rules (AND/OR)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {conditionType === 'simple' && (
                  <div className="space-y-6">
                    <div className="space-y-2">
                      <Label>Question</Label>
                      <QuestionCombobox
                        value={simpleCondition.field}
                        options={questionOptions}
                        onChange={val =>
                          setSimpleCondition(prev => ({ ...prev, field: val as string, value: '' }))
                        }
                        className="w-full"
                        placeholder="Select a question..."
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="operator">Operator</Label>
                      <Select
                        value={simpleCondition.operator}
                        onValueChange={op =>
                          setSimpleCondition(prev => ({
                            ...prev,
                            operator: op as ComparisonOperator,
                          }))
                        }
                      >
                        <SelectTrigger
                          id="operator"
                          className="w-full"
                        >
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {comparisonOperators.map(operator => (
                            <SelectItem
                              key={operator.value}
                              value={operator.value}
                            >
                              {operator.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    {showValueInput && (
                      <div className="space-y-2">
                        <Label htmlFor="value">Value</Label>
                        {selectedQuestion?.type === 'select' ||
                        selectedQuestion?.type === 'radio' ? (
                          <Select
                            value={(simpleCondition.value as string) || ''}
                            onValueChange={val =>
                              setSimpleCondition(prev => ({ ...prev, value: val }))
                            }
                          >
                            <SelectTrigger
                              id="value"
                              className="w-full"
                            >
                              <SelectValue placeholder="Select value" />
                            </SelectTrigger>
                            <SelectContent>
                              {selectedQuestion.options?.map(option => (
                                <SelectItem
                                  key={option.id}
                                  value={option.id}
                                >
                                  {option.value}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        ) : (
                          <Input
                            value={(simpleCondition.value as string) || ''}
                            onChange={event =>
                              setSimpleCondition(prev => ({ ...prev, value: event.target.value }))
                            }
                            placeholder="Enter value"
                          />
                        )}
                      </div>
                    )}
                  </div>
                )}

                {conditionType === 'logical' && (
                  <div className="space-y-6">
                    <div className="space-y-2">
                      <Label htmlFor="combined-rules">Combine Rules With</Label>
                      <Select
                        value={logicalCondition.operator}
                        onValueChange={op =>
                          setLogicalCondition(prev => ({ ...prev, operator: op as 'and' | 'or' }))
                        }
                      >
                        <SelectTrigger
                          id="combined-rules"
                          className="w-full"
                        >
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="and">AND</SelectItem>
                          <SelectItem value="or">OR</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="space-y-2">
                      {logicalCondition.conditions.map((condition, index) => {
                        if ('conditions' in condition) return null;
                        const fieldCondition = condition as FieldCondition;
                        const conditionQuestion = questions[fieldCondition.field];
                        const showVal = !['empty', 'notEmpty'].includes(fieldCondition.operator);

                        return (
                          <div
                            key={index}
                            className="bg-muted/50 flex items-start gap-2 rounded-lg border p-2"
                          >
                            <div className="flex-1 space-y-2">
                              <QuestionCombobox
                                value={fieldCondition.field}
                                options={questionOptions}
                                onChange={val =>
                                  handleUpdateLogicalCondition(index, {
                                    field: val as string,
                                    value: '',
                                  })
                                }
                                placeholder="Select a question..."
                                className="w-full"
                              />
                              <div className="space-y-2">
                                <Select
                                  value={fieldCondition.operator}
                                  onValueChange={op =>
                                    handleUpdateLogicalCondition(index, {
                                      operator: op as ComparisonOperator,
                                    })
                                  }
                                >
                                  <SelectTrigger className="w-full">
                                    <SelectValue />
                                  </SelectTrigger>
                                  <SelectContent>
                                    {comparisonOperators.map(operator => (
                                      <SelectItem
                                        key={operator.value}
                                        value={operator.value}
                                      >
                                        {operator.label}
                                      </SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                                {showVal &&
                                  (conditionQuestion?.type === 'select' ||
                                  conditionQuestion?.type === 'radio' ? (
                                    <Select
                                      value={(fieldCondition.value as string) || ''}
                                      onValueChange={val =>
                                        handleUpdateLogicalCondition(index, { value: val })
                                      }
                                    >
                                      <SelectTrigger className="w-full">
                                        <SelectValue placeholder="Select value" />
                                      </SelectTrigger>
                                      <SelectContent>
                                        {conditionQuestion.options?.map(option => (
                                          <SelectItem
                                            key={option.id}
                                            value={option.id}
                                          >
                                            {option.value}
                                          </SelectItem>
                                        ))}
                                      </SelectContent>
                                    </Select>
                                  ) : (
                                    <Input
                                      value={(fieldCondition.value as string) || ''}
                                      onChange={event =>
                                        handleUpdateLogicalCondition(index, {
                                          value: event.target.value,
                                        })
                                      }
                                      placeholder="Value"
                                      className="w-full"
                                    />
                                  ))}
                              </div>
                            </div>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleRemoveLogicalCondition(index)}
                            >
                              <X />
                            </Button>
                          </div>
                        );
                      })}
                    </div>

                    <Button
                      variant="outline"
                      className="w-full"
                      onClick={handleAddLogicalCondition}
                    >
                      <Plus />
                      Add Condition
                    </Button>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={onClose}
          >
            Cancel
          </Button>
          <Button onClick={handleSave}>Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
